// CandidatesZone — people named by evidence who are not on the record (§10, §13).
//
// Extracted from round 1's PeoplePage so the People page and the Campaigns page show the same
// list with the same verb, and so a lookalike run has somewhere to land. It keeps PeoplePage's
// behaviour exactly: Promote holds the candidate's proposal when there is one (P1's one door —
// the applier writes the person), and flips a `persons` row that has no proposal behind it.
//
// What round 2 adds is the PERSONA: a candidate a lookalike run produced names the segment it
// came from, and carries Surfe's own words as the quote that justified naming them at all.
// Nothing here is on the record, so every row carries ◇.
import { useCallback, useEffect, useState } from 'react';
import { Att, Ev, Fig, Ref, Sep, Sheet, Tag, Zone, ZoneHead } from './PageKit.js';
import { EvidenceSrc } from './ProfileZone.js';
import { useLocale, useT } from '../i18n/index.js';
import { ROUTES, open } from '../routes.js';
import { listCandidates, promote, type CandidateRow } from '../api/personas.js';
import { ApiError } from '../api/client.js';
import { useToast } from './Toast.js';
import { tCtx } from '../../shared/i18n.js';
import './CandidatesZone.de.js';
import './CandidatesZone.fr.js';

/**
 * WHERE A CANDIDATE CAME FROM (BUILD-BRIEF-R3b §1, §3).
 *
 * Round 2 had one producer — a lookalike run against a segment persona — so the persona name
 * was the whole story. Round 4 has one other: a RESEARCH run (`deep_research`) reads the
 * organisation's own pages and the press and proposes the people it meets there. A queue that
 * mixes the two without saying which is which asks the operator to promote a stranger on trust,
 * so the source is a column.
 *
 * The fields below are read DEFENSIVELY off the candidate row: a server that names the source
 * outright is believed, and one that does not is read from the principal that filed the
 * proposal (`run:58`) plus the organisation the candidate was read at. Where neither says
 * anything, the cell reads "not collected" — never a source invented to fill it.
 */
interface CandidateSourceFields {
  source?: string | null;
  organisation_ref?: string | null;
  run_ref?: string | null;
  /** The evidence's own kind when the server names it (`snapshot` | `url` | …). Read
   *  defensively: the candidates route does not send it today, and the shape of the ref
   *  answers the same question — see `CandidateEvidenceSrc`. */
  evidence_kind?: string | null;
}

function SourceCell({ c }: { c: CandidateRow }) {
  const t = useT();
  const extra = c as CandidateRow & CandidateSourceFields;
  const principal = (c.proposal_principal ?? '').trim();
  const fromRun = /^run:/.test(principal);
  const source = (extra.source ?? '').trim();
  const orgRef = (extra.organisation_ref ?? '').trim();
  const runRef = (extra.run_ref ?? '').trim();

  if (c.persona_name) {
    return <span title={t('a lookalike run against this segment persona named them')}>{c.persona_name}</span>;
  }
  if (source === 'research' || fromRun) {
    return (
      <span title={runRef || principal || undefined}>
        {tCtx(t, 'research', 'source')}
        {orgRef ? <> <span className="sep">·</span> <Ref>{orgRef}</Ref></>
          : c.organisation_name ? <> <span className="sep">·</span> {c.organisation_name}</> : null}
      </span>
    );
  }
  if (principal) return <Ref title={t('the principal that filed the proposal')}>{principal}</Ref>;
  return <span className="fv--unknown">{t('not collected')}</span>;
}

/**
 * WHERE THE QUOTE CAME FROM (BUILD-BRIEF-R3b §1).
 *
 * Round 2 had ONE producer, so the caption could be a constant: a lookalike candidate's evidence
 * is a LinkedIn URL and the words beside it are Surfe's own. Round 3b adds a producer whose
 * evidence is a SNAPSHOT of a page this record fetched itself, and a constant caption then
 * attributes a snapshot quote to a vendor that never saw it — a provenance claim the record
 * cannot support (P2). So the caption FOLLOWS THE EVIDENCE:
 *
 *  - a snapshot (the server names the kind, or the ref is a snapshot id) resolves through the
 *    same `EvidenceSrc` the profile brief and the compare sheet use: the page's own URL, and the
 *    date it was fetched. Unresolved, it degrades to the bare ref — never to a wrong caption;
 *  - a URL is the link itself, and "as Surfe returned it" is added ONLY for a lookalike
 *    candidate, the one producer for which that sentence is true;
 *  - anything else is the ref, said plainly.
 *
 * `evidence_ref` is never an `href` again: a snapshot id in an href is a relative link to `/27`.
 * The choice itself is `candidateEvidenceSource`, a pure function, so it can be read and checked
 * without a browser.
 */
export function candidateEvidenceSource(c: CandidateRow): {
  kind: 'none' | 'snapshot' | 'url' | 'ref';
  ref: string;
  surfe: boolean;
} {
  const extra = c as CandidateRow & CandidateSourceFields;
  const ref = (c.evidence_ref ?? '').trim();
  const named = (extra.evidence_kind ?? '').trim();
  const source = (extra.source ?? '').trim();
  const principal = (c.proposal_principal ?? '').trim();
  const surfe = source === 'lookalike' || !!c.persona_name || /^lookalike:/.test(principal);
  if (!ref) return { kind: 'none', ref, surfe };
  if (named === 'snapshot' || (!named && /^[0-9]+$/.test(ref))) return { kind: 'snapshot', ref, surfe };
  if (/^https?:\/\//i.test(ref)) return { kind: 'url', ref, surfe };
  return { kind: 'ref', ref, surfe };
}

export function CandidateEvidenceSrc({ c, fmtDate }: {
  c: CandidateRow;
  fmtDate: (iso: string | null | undefined) => string;
}) {
  const t = useT();
  const { kind, ref, surfe } = candidateEvidenceSource(c);

  return (
    <>
      <Att kind="quoted" />
      {kind === 'none' ? <span className="d">{t('no source named')}</span>
        : kind === 'snapshot' ? <EvidenceSrc evidenceKind="snapshot" evidenceRef={ref} fmtDate={fmtDate} />
          : kind === 'url' ? (
            <>
              <a className="lnk" href={ref} target="_blank" rel="noreferrer noopener"><Ref>{ref}</Ref></a>
              {surfe ? <><Sep />{t('as Surfe returned it')}</> : null}
            </>
          )
            : <Ref>{ref}</Ref>}
    </>
  );
}

export interface CandidatesZoneProps {
  /** Called after a promotion, so the page that owns the board can reload it. */
  onPromote?: () => void;
  /** The page's search box, when it has one. A filter, never a fetch. */
  query?: string;
}

export default function CandidatesZone({ onPromote, query }: CandidatesZoneProps) {
  const t = useT();
  const { fmtDate } = useLocale();
  const toast = useToast();

  const [items, setItems] = useState<CandidateRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const answer = await listCandidates();
      setItems(answer.items);
      setError(null);
    } catch (err) {
      // A failed read is silence: no list, no zero.
      setItems(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const needle = (query ?? '').trim().toLowerCase();
  const shown = needle
    ? (items ?? []).filter((c) => [c.name, c.organisation_name, c.role_title, c.ref, c.persona_name]
      .some((f) => (f ?? '').toLowerCase().includes(needle)))
    : (items ?? []);

  const act = useCallback(async (candidate: CandidateRow) => {
    setBusy(candidate.key);
    try {
      await promote(candidate);
      toast('ok', candidate.proposal_ref
        ? t('{name} is on the record. {ref} is held.', { name: candidate.name, ref: candidate.proposal_ref })
        : t('{name} is on the record.', { name: candidate.name }));
      await load();
      onPromote?.();
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not promote this candidate.'));
    } finally {
      setBusy(null);
    }
  }, [load, onPromote, t, toast]);

  return (
    <Zone id="candidates">
      <ZoneHead
        label={t('Candidates')}
        count={items ? <Fig>{shown.length}</Fig> : undefined}
        note={t('named by evidence, not on the record · promote or refuse in Review')}
        route={
          <button type="button" className="zone-route" onClick={() => open(ROUTES.review({ kind: 'candidate' }))}>
            {t('Review')} →
          </button>
        }
      />
      <Sheet clip={!!shown.length} pad={!shown.length}>
        {error ? (
          <p className="zone-empty">{t('The candidates could not be read: {error}', { error })}</p>
        ) : items === null ? (
          <p className="zone-empty">{t('loading…')}</p>
        ) : shown.length === 0 ? (
          <p className="zone-empty">
            {items.length
              ? t('No candidate matches the search. {n} are waiting.', { n: String(items.length) })
              : t('Nothing has proposed itself as a person. Candidates arrive from a lookalike run or a registry publication, and they stay off the record until you promote one.')}
          </p>
        ) : (
          shown.map((c) => (
            <div key={c.key} className="row" style={{ gridTemplateColumns: '1fr 150px 200px auto', background: 'var(--sheet-2)' }}>
              <span>
                <span className="w" style={{ color: 'var(--ink-2)' }}>{c.name}</span>
                <span className="d">
                  {c.role_title && <> · {c.role_title}</>}
                  {c.organisation_name && <> · {c.organisation_name}</>}
                  {c.ref && <>{' · '}<Ref>{c.ref}</Ref></>}
                </span>
                <br />
                <Tag tone="ghost"><Att kind="proposed" /> {t('not on the record')}</Tag>
                {c.persona_name && (
                  <Tag tone="ghost">
                    {t('from the segment {name}', { name: c.persona_name })}
                  </Tag>
                )}
                {c.quote && (
                  <Ev src={<CandidateEvidenceSrc c={c} fmtDate={fmtDate} />}>
                    {c.quote}
                  </Ev>
                )}
              </span>
              {/* the source column: who named this person, and where they read it */}
              <span className="d">
                <span className="u-label">{t('Source')}</span>
                <br />
                <SourceCell c={c} />
              </span>
              <span className="d">
                {c.note}
                <br />
                <Fig>{fmtDate(c.created_at)}</Fig>
              </span>
              <button
                type="button"
                className="btn btn-sm"
                disabled={busy === c.key}
                onClick={() => { void act(c); }}
                title={c.proposal_ref
                  ? t('Holds {ref} and writes the row', { ref: c.proposal_ref })
                  : t('No proposal behind this row — its status is flipped directly')}
              >
                {busy === c.key ? t('Saving…') : t('Promote to person')}
              </button>
            </div>
          ))
        )}
      </Sheet>
    </Zone>
  );
}
